import { useNavigate } from 'react-router-dom';
import { useMemberPhoto } from '@/hooks';
import { getMemberDisplayName } from '@/utils';
import { Member } from '@/types/member';


interface MemberNameCellProps {
  memberId: string;
  memberName: string;
  memberEmail: string;
  showPhoto?: boolean;
  showEmail?: boolean;
  className?: string;
  isHistorical?: boolean;
  historicalData?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    deleted_at?: string | null;
    anonymized_at?: string | null;
  } | null;
  // Optional member object for proper deleted member detection
  member?: Member | null;
  // Optional externally supplied photo data to prevent redundant fetches.
  // Nullable because signing a single variant can fail on its own, which is
  // also why `large_photo_url` is here as a fallback — same pattern as
  // MembersCardView and MemberSearchableDropdown.
  photo?: {
    thumbnail_photo_url: string | null;
    large_photo_url?: string | null;
  } | null;
  isPhotoLoading?: boolean;
}

export default function MemberNameCell({
  memberId,
  memberName,
  memberEmail,
  showPhoto = true,
  showEmail = true,
  className = "",
  isHistorical = false,
  historicalData = null,
  member = null,
  photo: externalPhoto,
  isPhotoLoading: externalIsPhotoLoading
}: MemberNameCellProps) {
  const navigate = useNavigate();
  
  // Prefer photo data supplied by the parent. Anything rendering a LIST must
  // supply it (via one `useBulkMemberPhotos` call for the whole list) — a cell
  // is not the right place to decide how a table fetches.
  //
  // The fallback below is for the single-member surfaces that have no list to
  // inherit from (a membership's member row, a confirmation modal). It shares
  // the per-member photo cache with every bulk fetch, so it is normally free:
  // the member was already cached by whatever list the admin navigated in from.
  const shouldFetchPhoto = showPhoto && !externalPhoto && externalIsPhotoLoading === undefined;
  const { photo: fetchedPhoto, isLoading: internalIsPhotoLoading } = useMemberPhoto(
    shouldFetchPhoto ? memberId : null
  );

  // Use external photo data if available, otherwise fall back to internal fetch
  const photo = externalPhoto || fetchedPhoto;
  const isPhotoLoading = externalIsPhotoLoading !== undefined ? externalIsPhotoLoading : internalIsPhotoLoading;
  // 32px avatar, so the 150px thumbnail is the right size; `large` only covers
  // the case where that one path failed to sign.
  const photoUrl = photo?.thumbnail_photo_url ?? photo?.large_photo_url ?? null;

  // Calculate display name with proper deleted member handling
  const displayName = (() => {
    // If we have the full member object, use the utility function for proper deleted member handling
    if (member) {
      return getMemberDisplayName(member);
    }
    
    // For historical data, check if it represents a deleted member
    if (isHistorical && historicalData) {
      // If historicalData has deletion info, it's a deleted member
      if (historicalData.deleted_at) {
        return 'Deleted Member';
      }
      return `${historicalData.first_name} ${historicalData.last_name}`;
    }
    
    // Fallback to provided memberName, with 'Unknown Member' as last resort
    return memberName || 'Unknown Member';
  })();

  const displayEmail = isHistorical && historicalData 
    ? historicalData.email 
    : memberEmail;


  const handlePhotoClick = () => {
    // Prevent navigation if memberId is not available
    if (!memberId) {
      return;
    }
    navigate(`/members/${memberId}`);
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {showPhoto && (
        <div className="flex-shrink-0">
          <button type="button"
            onClick={handlePhotoClick}
            className="cursor-pointer hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-full"
            title={`View ${displayName}'s profile`}
          >
            {isPhotoLoading ? (
              <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
            ) : photoUrl ? (
              <img
                src={photoUrl}
                alt={displayName}
                className="h-8 w-8 rounded-full object-cover"
                loading="lazy"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                <span className="text-xs font-medium text-gray-500">
                  {displayName ? displayName.split(' ').map(n => n[0]).join('').toUpperCase() : '?'}
                </span>
              </div>
            )}
          </button>
        </div>
      )}
      
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">
          {displayName}
        </p>
        {isHistorical && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            Historical data (member now deleted)
          </span>
        )}
        {showEmail && (
          <p className="text-sm text-gray-500 truncate">
            {displayEmail || 'No email'}
          </p>
        )}
      </div>
    </div>
  );
}